import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnimateOnView } from '../../hooks/useIntersectionObserver';
import { useTerapeutas } from '../../hooks/useTerapeutas';
import './ProfessionalsSection.css';

const ProfessionalsSection: React.FC = () => {
  const navigate = useNavigate();
  const [ref, shouldAnimate] = useAnimateOnView();
  const { terapeutas, loading, error } = useTerapeutas();

  return (
    <section
      ref={ref}
      className={`professionals-section section-padding${shouldAnimate ? ' loaded' : ''}`}
      id="professionals"
    >
      <div className="container">
        <div className="section-header ps-header">
          <span className="ps-eyebrow">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3 L14.5 8.5 L20.5 9.3 L16 13.3 L17.3 19.3 L12 16.1 L6.7 19.3 L8 13.3 L3.5 9.3 L9.5 8.5 Z" />
            </svg>
            Nuestro equipo
          </span>
          <h2 className="section-title ps-title">
            <span>Quién te va a escuchar</span>
          </h2>
          <p className="section-subtitle ps-subtitle">
            Psicólogos especializados en terapia de parejas e individual
          </p>
        </div>

        {loading ? (
          <div className="ps-state animate-in" style={{ '--delay': '0.2s' } as React.CSSProperties}>
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
            <p>Cargando información de nuestros profesionales...</p>
          </div>
        ) : error ? (
          <div className="ps-state animate-in" style={{ '--delay': '0.2s' } as React.CSSProperties}>
            <div className="ps-state__icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <p>Error al cargar información de profesionales: {error}</p>
            <button className="ps-btn ps-btn--outline" onClick={() => window.location.reload()}>
              <i className="fas fa-refresh"></i>
              Intentar nuevamente
            </button>
          </div>
        ) : terapeutas && terapeutas.length > 0 ? (
          <div className="ps-grid">
            {terapeutas.map((professional, index) => (
              <article
                key={professional.id}
                className="ps-card animate-in"
                style={{ '--delay': `${0.2 + index * 0.1}s` } as React.CSSProperties}
              >
                <div className="ps-card__photo">
                  <img
                    src={professional.image}
                    alt={`${professional.name} - ${professional.title}`}
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/assets/img/placeholder-therapist.webp';
                    }}
                  />
                </div>
                <div className="ps-card__body">
                  <h3 className="ps-card__name">{professional.name}</h3>
                  <span className="ps-card__title">{professional.title}</span>
                  <p className="ps-card__desc">{professional.description}</p>

                  {professional.specialties && professional.specialties.length > 0 && (
                    <ul className="ps-card__tags">
                      {professional.specialties.slice(0, 4).map((specialty, i) => (
                        <li key={i}>{specialty}</li>
                      ))}
                      {professional.specialties.length > 4 && (
                        <li className="ps-card__tags-more">
                          +{professional.specialties.length - 4} más
                        </li>
                      )}
                    </ul>
                  )}

                  <div className="ps-card__actions">
                    <a
                      href={`/terapista/${professional.id}`}
                      className="ps-btn ps-btn--outline"
                      aria-label={`Ver perfil completo de ${professional.name}`}
                      onClick={(e) => { e.preventDefault(); navigate(`/terapista/${professional.id}`); }}
                    >
                      <i className="fas fa-user"></i>
                      Ver Perfil
                    </a>
                    <a
                      href={professional.bookingUrl}
                      className="ps-btn ps-btn--primary"
                      aria-label={`Agendar cita con ${professional.name}`}
                      onClick={(e) => { e.preventDefault(); navigate(professional.bookingUrl || '/agendar'); }}
                    >
                      <i className="fas fa-calendar-check"></i>
                      Agendar
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="ps-state animate-in" style={{ '--delay': '0.2s' } as React.CSSProperties}>
            <div className="ps-state__icon">
              <i className="fas fa-info-circle"></i>
            </div>
            <p>Pronto tendremos información de nuestros profesionales disponible.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfessionalsSection;
